import { ModalBody, ModalFooter, ModalHeader } from "@nextui-org/react";
import { colors } from "../../../../styles/_variables";
import Alert from "../../../Alert/Alert";
import Button from "../../../Button/Button";
import Checkbox from "../../../Checkbox/Checkbox";
import { useState } from "react";
import styles from "./CreateAccountModal.module.scss";
import { updateAction } from "../../../../store/slices/unifiedBalanceSlice";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { addMessage } from "../../../../utils/addMessage";
import { genericErrorMessage } from "../../../../utils";
import { approveUnification } from "../../../../connectors/unifiedBalanceApi";
import { useLocation } from "react-router-dom";
import { openExternal } from "../../../NavBar/utils/utils";
import useScreenSize from "../../../../hooks/useScreenSize";

const CreateAccountModal = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const staticUrl = import.meta.env.VITE_BASE_URL_STATIC;

  const {
    unifiedBalance: {
      assessmentData: { assessment, mainHolders },
      selectedAccount,
    },
    authentication: { user },
  } = useAppSelector((state) => state);
  const { checkboxText, checkboxStyle, alertText, subtitle, modalHeader, modalFooterMobile, modalMobileHeader } = styles;
  const { isMobile } = useScreenSize();

  const email = queryParams.get("email") || user?.email || "";

  const [acceptedTerms, setAcceptedTerms] = useState<"YES" | "NO">("NO");
  const [loading, setLoading] = useState(false);

  const handleUnification = async () => {
    setLoading(true);
    try {
      await approveUnification({
        email,
        accepted_tos: acceptedTerms === "YES",
        allaria_account_number: selectedAccount.number,
      });

      dispatch(updateAction("FEEDBACK_SUCCESS"));
    } catch (error) {
      addMessage({
        messageId: "allariaMasModal",
        message: genericErrorMessage,
        type: "error",
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ModalHeader className={modalMobileHeader}>
        {!isMobile ? (
          <h1
            style={{
              color: colors.primary[500],
              fontSize: "1.5rem",
              fontWeight: 600,
            }}
          >
            Crear cuenta en Allaria+
          </h1>
        ) : (
          <h1 className={modalHeader}>Crear cuenta en Allaria+</h1>
        )}
      </ModalHeader>
      <ModalBody>
        <p className={subtitle}>
          {assessment.required_action ===
          "CREATE_ACCOUNT_OWNER_FOR_ALL_COOWNERS" ? (
            <>
              Para unificar los saldos entre las plataformas es necesario crear
              una nueva cuenta con cotitularidad en Allaria+, aceptá los
              Términos y condiciones para finalizar con la apertura de tu
              cuenta:
            </>
          ) : (
            <>
              Para unificar los saldos entre las plataformas tenemos que crearte
              una cuenta en Allaria+, aceptá los Términos y condiciones para
              finalizar con la apertura de tu cuenta:
            </>
          )}
        </p>

        <Card
          showHeader
          title={selectedAccount.name.split("-")[0].trim()}
          subtitle={selectedAccount.number}
        />

        {assessment.required_action ===
          "CREATE_ACCOUNT_OWNER_FOR_ALL_COOWNERS" && (
          <>
            <p className={`${subtitle} mt-3`}>
              Todos los cotitulares serán invitados a la nueva cuenta en
              Allaria+ como propietarios. Les enviaremos la información tras la
              apertura:
            </p>

            {mainHolders.map(({ email, description }, i) => (
              <Card key={i} title={description} subtitle={email} />
            ))}
          </>
        )}

        <Alert variant="INFO" className="mt-3 mb-1">
          <p className={alertText}>
            Te daremos el alias y el CVU tras realizar la apertura
          </p>
        </Alert>

        <div className="flex items-start gap-1">
          <Checkbox
            classNames={
              acceptedTerms === "NO" ? { wrapper: checkboxStyle } : {}
            }
            value={acceptedTerms}
            isSelected={acceptedTerms === "YES"}
            onChange={() =>
              setAcceptedTerms(acceptedTerms === "YES" ? "NO" : "YES")
            }
          />
          <p
            className={checkboxText}
            onClick={() =>
              setAcceptedTerms(acceptedTerms === "YES" ? "NO" : "YES")
            }
          >
            Acepto los{" "}
            <span
              onClick={(e) => {
                e.stopPropagation();
                openExternal(
                  `${staticUrl}/terms/2)+TyC+Allaria%2B+Cuenta+%2B+Anexo+I+sin+aclaracion+(web+y+OB+Allaria%2B).pdf`,
                  "_blank"
                );
              }}
            >
              Términos y condiciones
            </span>{" "}
            y la unificación entre cuentas.
          </p>
        </div>
      </ModalBody>

      <ModalFooter
        className={
          !isMobile ? "flex justify-center items-center" : modalFooterMobile
        }
      >
        <div className={isMobile ? modalFooterMobile : "flex gap-4"}>
          <Button
            variant="secondary"
            text="Volver"
            onPress={() => dispatch(updateAction("ACCOUNTS_SELECTOR"))}
            className={isMobile ? "w-full mb-2.5" : ""}
          />
          <Button
            variant="primary"
            text="Crear cuenta"
            isDisabled={acceptedTerms === "NO"}
            isLoading={loading}
            onPress={handleUnification}
            className={isMobile ? "w-full" : ""}
          />
        </div>
      </ModalFooter>
    </>
  );
};

export default CreateAccountModal;

const Card = ({
  title,
  subtitle,
  showHeader,
}: {
  title: string;
  subtitle: string;
  showHeader?: boolean;
}) => {
  const { cardContainer } = styles;

  return (
    <div className={cardContainer}>
      {showHeader && <p>Cuenta Comitente</p>}
      <p>{title}</p>
      <p>{subtitle}</p>
    </div>
  );
};
